import { type RefObject, useRef, useState } from 'react';

import { useDebouncedCallback } from 'hooks';
import { round } from 'utils/math';

export interface ContainerDimensions {
	outerHeight: number;
	outerWidth: number;
	scrollHeight: number;
	// Scroll area size minus the container size, since scroll position is set
	// from the left edge. E.g. if a 700px container with 2000px of scrollable
	// content should be scrolled all the way to the end, that means setting
	// the scroll position to 1300px.
	scrollRangeX: number;
	scrollRangeY: number;
	scrollWidth: number;
}

/** X and Y position between 0 and 1, like a percentage. */
export interface NormalizedPoint {
	x: number;
	y: number;
}

export function useContainerDimensionsRef(
	containerRef: RefObject<HTMLElement | null>,
) {
	const containerDimensionsRef = useRef<ContainerDimensions>({
		outerHeight: 0,
		outerWidth: 0,
		scrollHeight: 0,
		scrollRangeX: 0,
		scrollWidth: 0,
		scrollRangeY: 0,
	});

	const updateContainerDimensions = () => {
		if (!containerRef.current) {
			return;
		}
		const { clientHeight, clientWidth, scrollHeight, scrollWidth } =
			containerRef.current;
		const scrollRangeX = scrollWidth - clientWidth;
		const scrollRangeY = scrollHeight - clientHeight;
		containerDimensionsRef.current = {
			outerHeight: clientHeight,
			outerWidth: clientWidth,
			scrollHeight,
			scrollWidth,
			scrollRangeX,
			scrollRangeY,
		};
	};

	return [containerDimensionsRef, updateContainerDimensions] as const;
}

/**
 * Get the rendered size and scale of an image (e.g. when shrunk with
 * object-fit).
 *
 * https://stackoverflow.com/a/70788130
 */
export function getRenderedImageSize(
	image: HTMLImageElement,
	fullWidth: number,
	fullHeight: number,
): {
	height: number;
	scale: number;
	width: number;
} {
	const { height, width } = image;
	// Could use image.naturalWidth and image.naturalHeight instead of passing
	// in the full width and height, but those can sometimes be zero (probably
	// due to re-renders) so being explicit is safer.
	const imageRatio = fullWidth / fullHeight;
	const containerRatio = width / height;
	const [renderedWidth, renderedHeight] =
		imageRatio >= containerRatio
			? [width, width / imageRatio]
			: [height * imageRatio, height];
	return {
		height: renderedHeight,
		width: renderedWidth,
		scale: renderedWidth / fullWidth,
	};
}

/** Scroll the specified element X pixels in the specified direction. */
export function pan({
	direction,
	distance,
	element,
}: {
	direction: 'down' | 'left' | 'right' | 'up';
	distance: number;
	element: HTMLElement | null | undefined;
}) {
	if (!element) {
		return;
	}
	const directionOffsets = {
		up: [0, -distance],
		down: [0, distance],
		left: [-distance, 0],
		right: [distance, 0],
	} as const;
	const [xOffset, yOffset] = directionOffsets[direction];
	element.scrollTo(element.scrollLeft + xOffset, element.scrollTop + yOffset);
}

export function useScrollPositionStatus(
	containerRef: RefObject<HTMLElement | null>,
	dimensionsRef: RefObject<ContainerDimensions>,
) {
	const [isAtBottom, setIsAtBottom] = useState(false);
	const [isAtLeft, setIsAtLeft] = useState(false);
	const [isAtRight, setIsAtRight] = useState(false);
	const [isAtTop, setIsAtTop] = useState(false);

	const [updateScrollPositionStatus] = useDebouncedCallback(() => {
		if (!containerRef.current || !dimensionsRef.current) {
			return;
		}
		const { scrollLeft, scrollTop } = containerRef.current;
		// Don't require being at the exact edge.
		const threshold = 10;
		setIsAtBottom(dimensionsRef.current.scrollRangeY - scrollTop < threshold);
		setIsAtLeft(scrollLeft < threshold);
		setIsAtRight(dimensionsRef.current.scrollRangeX - scrollLeft < threshold);
		setIsAtTop(scrollTop < threshold);
	}, 50);

	return {
		isAtBottom,
		isAtLeft,
		isAtRight,
		isAtTop,
		updateScrollPositionStatus,
	} as const;
}

/** Get the X and Y positions (between 0 and 1) of a mouse/pointer event. */
export function getPointerPositionPoint(
	element: HTMLElement,
	event: { clientX: number; clientY: number },
): NormalizedPoint {
	const rect = element.getBoundingClientRect();
	return {
		x: round(Math.floor(event.clientX - rect.x) / rect.width, 4),
		y: round(Math.floor(event.clientY - rect.y) / rect.height, 4),
	};
}

/**
 * Get the X and Y distance (between 0 and 1) of how far the container is
 * scrolled (i.e. 0 == fully top/left, 1 == fully bottom/right). Falls back to
 * the center (0.5) if not scrollable.
 */
export function getScrollPositionPoint(
	container: HTMLElement,
	dimensions: ContainerDimensions,
): NormalizedPoint {
	const { scrollLeft, scrollTop } = container;
	const { scrollRangeX, scrollRangeY } = dimensions;
	return {
		x: scrollRangeX > 0 ? scrollLeft / scrollRangeX : 0.5,
		y: scrollRangeY > 0 ? scrollTop / scrollRangeY : 0.5,
	};
}

export function scrollToMousePosition({
	container,
	dimensions,
	mouseEvent,
}: {
	container: HTMLElement;
	dimensions: ContainerDimensions;
	mouseEvent: MouseEvent;
}) {
	const {
		outerHeight,
		outerWidth,
		scrollHeight,
		scrollRangeX,
		scrollRangeY,
		scrollWidth,
	} = dimensions;
	// Ratios for the scrollable area compared to the container.
	const scrollWidthRatio = scrollWidth / outerWidth;
	const scrollHeightRatio = scrollHeight / outerHeight;
	// The mouse event offsets are the positions inside the scrollable
	// area, get the equivalent container positions. (E.g. a 500x500 container
	// has 1800x1200 content and a mouse position is x:1452, y:726.)
	const outerMouseX = round(mouseEvent.offsetX / scrollWidthRatio);
	const outerMouseY = round(mouseEvent.offsetY / scrollHeightRatio);
	const rangeWidthRatio = scrollRangeX / outerWidth;
	const rangeHeightRatio = scrollRangeY / outerHeight;
	container.scrollTo(
		round(outerMouseX * rangeWidthRatio),
		round(outerMouseY * rangeHeightRatio),
	);
}

/** Get the next zoom in width step. */
export function getZoomInWidth({
	currentMaxWidth,
	fullImageWidth,
}: {
	currentMaxWidth: number;
	fullImageWidth: number;
}): number {
	// Double the current width. If the new width is close enough to the full
	// width, zoom all the way in instead of leaving a tiny last zoom step.
	// Also not allowed to go above the full width.
	const newMaxWidth = currentMaxWidth * 2;
	const threshold = fullImageWidth / 4;
	return fullImageWidth - newMaxWidth <= threshold
		? fullImageWidth
		: newMaxWidth;
}

function getMinImageWidth({
	containerHeight,
	containerWidth,
	fullImageHeight,
	fullImageWidth,
}: {
	containerHeight: number;
	containerWidth: number;
	fullImageHeight: number;
	fullImageWidth: number;
}): number {
	// Can't just use container width since the image may be height constrained.
	const imageRatio = fullImageWidth / fullImageHeight;
	const containerRatio = containerWidth / containerHeight;
	const constraintDirection =
		imageRatio > containerRatio ? 'horizontal' : 'vertical';
	return constraintDirection === 'horizontal'
		? containerWidth
		: containerHeight * imageRatio;
}

/** Get the next zoom out width step. */
export function getZoomOutWidth({
	containerHeight,
	containerWidth,
	currentMaxWidth,
	fullImageHeight,
	fullImageWidth,
}: {
	containerHeight: number;
	containerWidth: number;
	currentMaxWidth: number;
	fullImageHeight: number;
	fullImageWidth: number;
}): number | undefined {
	// Halve the current width. If the new width is close enough to the minimum
	// width, zoom all the way out instead of leaving a tiny last zoom step.
	const newMaxWidth = currentMaxWidth / 2;
	const minWidth = getMinImageWidth({
		containerHeight,
		containerWidth,
		fullImageHeight,
		fullImageWidth,
	});
	const threshold = minWidth / 3;
	return newMaxWidth - minWidth <= threshold ? undefined : newMaxWidth;
}
